import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import dateUtils from '../../utils/dateUtils';
import './CommonStyles.css';
import './AddSellStyles.css';
import './ProductSearchStyles.css';

const today = () => dateUtils.getChicagoISOString().split('T')[0];

const normalizeOptions = (value) => {
  if (Array.isArray(value)) return value.map((item) => (typeof item === 'object' ? item : { id: item, name: item }));
  if (value && typeof value === 'object') {
    return Object.entries(value).map(([id, name]) => ({ id, name }));
  }
  return [];
};

const AddPurchaseReturn = ({ onBack }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState({
    contact_id: '',
    location_id: '',
    ref_no: '',
    transaction_date: today(),
    tax_id: '',
    tax_amount: 0,
    products: [],
    document: null,
  });

  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await apiService.getCombinedPurchaseReturnCreateData();
        const data = result.data || result;
        setLocations(normalizeOptions(data.business_locations));
        setTaxes(data.taxes || []);
      } catch (err) {
        /* console.error('Error fetching purchase return create data:', err); */
      }
      try {
        const res = await apiService.getContacts({ type: 'supplier', per_page: 100 });
        const list = Array.isArray(res) ? res : (res.data || []);
        setSuppliers(list);
      } catch (err) {
        /* console.error('Error fetching suppliers:', err); */
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!id) return;
    const fetchEditData = async () => {
      setIsLoadingEdit(true);
      try {
        const result = await apiService.getCombinedPurchaseReturnEditData(id);
        const data = result.data || result;
        const purchaseReturn = data.purchase_return || data;
        const lines = data.purchase_lines || purchaseReturn.purchase_lines || [];
        /* console.log('PR edit raw response:', result); */
        /* console.log('PR edit debug counts:', { */
        //   rawCount: data.debug_raw_count,
        //   joinCount: data.debug_join_count,
        // }); */
        /* console.log('PR edit lines:', lines); */
        setForm({
          contact_id: purchaseReturn.contact_id ? String(purchaseReturn.contact_id) : '',
          location_id: purchaseReturn.location_id ? String(purchaseReturn.location_id) : '',
          ref_no: purchaseReturn.ref_no || '',
          transaction_date: purchaseReturn.transaction_date ? purchaseReturn.transaction_date.split(' ')[0] : today(),
          tax_id: purchaseReturn.tax_id ? String(purchaseReturn.tax_id) : '',
          tax_amount: parseFloat(purchaseReturn.tax_amount) || 0,
          products: lines.map((line, idx) => ({
            id: Date.now() + idx,
            product_id: line.product_id,
            variation_id: line.variation_id,
            purchase_line_id: line.purchase_line_id || line.id,
            name: line.product_name || line.product?.name || '',
            sku: line.sub_sku || line.product?.sku || line.product?.sub_sku || '',
            quantity: parseFloat(line.quantity_returned || line.quantity) || 0,
            unit_price: parseFloat(line.purchase_price_inc_tax || line.purchase_price || line.pp_without_discount) || 0,
            lot_number: line.lot_number || '',
            exp_date: line.exp_date ? line.exp_date.split(' ')[0] : '',
          })),
          document: null,
        });
      } catch (err) {
        /* console.error('Error fetching purchase return edit data:', err); */
        toast.error('Failed to load purchase return data');
      } finally {
        setIsLoadingEdit(false);
      }
    };
    fetchEditData();
  }, [id]);

  const searchProducts = async (term) => {
    if (!term || !term.trim()) {
      setProductOptions([]);
      return;
    }
    setIsLoadingProducts(true);
    try {
      const res = await apiService.getProducts({ search: term, per_page: 50 });
      let list = Array.isArray(res) ? res : (res?.data || []);

      // Secondary client-side fuzzy match check if API returned full list
      if (term.trim() && list.length > 0) {
        const lower = term.toLowerCase().trim();
        const matched = list.filter((p) => {
          const name = (p.name || p.product || p.product_name || '').toLowerCase();
          const sku = (p.sku || p.sub_sku || '').toLowerCase();
          const varSku = (p.product_variations?.[0]?.variations?.[0]?.sub_sku || '').toLowerCase();
          return name.includes(lower) || sku.includes(lower) || varSku.includes(lower);
        });
        if (matched.length > 0) {
          list = matched;
        }
      }
      setProductOptions(list);
    } catch (err) {
      /* console.error('Error searching products:', err); */
      setProductOptions([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchProducts(productSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [productSearchTerm]);


  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addProduct = (product) => {
    const exists = form.products.find((p) => String(p.product_id) === String(product.id));
    if (exists) {
      toast.info('Product already added');
      setProductSearchTerm('');
      return;
    }
    const variation = product.product_variations?.[0]?.variations?.[0] || {};
    const unitPrice = parseFloat(
      product.purchase_price_inc_tax ||
      product.default_purchase_price ||
      variation.dpp_inc_tax ||
      variation.default_purchase_price ||
      product.purchase_price ||
      0
    );
    setForm((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          id: Date.now(),
          product_id: product.id,
          variation_id: variation.id || product.variation_id || product.id,
          name: product.name || product.product || 'Product',
          sku: product.sku || product.sub_sku || variation.sub_sku || '',
          quantity: 1,
          unit_price: unitPrice,
          lot_number: '',
          exp_date: '',
        },
      ],
    }));
    setProductSearchTerm('');
  };


  const updateProduct = (id, key, value) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.map((p) => (p.id !== id ? p : { ...p, [key]: value })),
    }));
  };

  const removeProduct = (id) => setForm((prev) => ({ ...prev, products: prev.products.filter((p) => p.id !== id) }));

  const subtotal = form.products.reduce((s, p) => s + (parseFloat(p.quantity) || 0) * (parseFloat(p.unit_price) || 0), 0);
  const selectedTax = taxes.find((t) => String(t.id) === String(form.tax_id));
  const taxRate = selectedTax ? parseFloat(selectedTax.amount) || 0 : 0;
  const taxAmt = subtotal * taxRate / 100;
  const finalTotal = subtotal + taxAmt;

  const validate = () => {
    const newErrors = {};
    if (!form.contact_id) newErrors.contact_id = 'Supplier is required';
    if (!form.location_id) newErrors.location_id = 'Business location is required';
    if (!form.transaction_date) newErrors.transaction_date = 'Date is required';
    if (form.products.length === 0) newErrors.products = 'Please add at least one product';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const payload = {
        ...form,
        final_total: finalTotal.toFixed(2),
        tax_amount: taxAmt.toFixed(2),
        products: form.products.map((p) => ({
          product_id: p.product_id,
          variation_id: p.variation_id,
          purchase_line_id: p.purchase_line_id || null,
          quantity: p.quantity,
          unit_price: p.unit_price,
          lot_number: p.lot_number,
          exp_date: p.exp_date,
        })),
      };
      if (id) payload.purchase_return_id = id;
      const result = id
        ? await apiService.updateCombinedPurchaseReturn(id, payload)
        : await apiService.createCombinedPurchaseReturn(payload);
      if (result.success) {
        toast.success(`Purchase return ${id ? 'updated' : 'saved'} successfully.`);
        if (onBack) onBack();
        else navigate('/purchase_return');
      } else {
        toast.error(result.message || `Failed to ${id ? 'update' : 'save'} purchase return`);
      }
    } catch (err) {
      toast.error(err.message || `Failed to ${id ? 'update' : 'save'} purchase return`);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingEdit) {
    return (
      <div className="sell-form-container add-sell-override" style={{ overflowX: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', margin: '0 auto', border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '1rem', color: '#475569' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sell-form-container add-sell-override" style={{ overflowX: 'hidden' }}>
      <div className="page-header">
        <button
          type="button"
          onClick={onBack || (() => navigate('/purchase_return'))}
          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', height: '40px' }}
          title="Back to Purchases"
        >←</button>
        <h2 style={{ fontSize: '1.25rem' }}>{id ? 'Edit' : 'Add'} Purchase Return</h2>
      </div>

      <form className="sell-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="form-section-content">
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
            <div className="form-group">
              <label>Supplier: <span style={{ color: '#ef4444' }}>*</span></label>
              <select
                className={`form-select ${errors.contact_id ? 'error' : ''}`}
                value={form.contact_id}
                onChange={(e) => handleChange('contact_id', e.target.value)}
              >
                <option value="">Please Select</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.supplier_business_name || s.name}</option>
                ))}
              </select>
              {errors.contact_id && <span className="error-text">{errors.contact_id}</span>}
            </div>

            <div className="form-group">
              <label>Business Location: <span style={{ color: '#ef4444' }}>*</span></label>
              <select
                className={`form-select ${errors.location_id ? 'error' : ''}`}
                value={form.location_id}
                onChange={(e) => handleChange('location_id', e.target.value)}
              >
                <option value="">Please Select</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
              {errors.location_id && <span className="error-text">{errors.location_id}</span>}
            </div>

            <div className="form-group">
              <label>Reference No:</label>
              <input type="text" className="form-input" value={form.ref_no} onChange={(e) => handleChange('ref_no', e.target.value)} />
            </div>

            <div className="form-group">
              <label>Date: <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="date"
                className={`form-input ${errors.transaction_date ? 'error' : ''}`}
                value={form.transaction_date}
                onChange={(e) => handleChange('transaction_date', e.target.value)}
              />
              {errors.transaction_date && <span className="error-text">{errors.transaction_date}</span>}
            </div>

            <div className="form-group" style={{ minWidth: 0, overflow: 'hidden' }}>
              <label>Attach Document:</label>
              <input
                type="file"
                className="form-input"
                accept=".pdf,.csv,.zip,.doc,.docx,.jpeg,.jpg,.png"
                onChange={(e) => handleChange('document', e.target.files[0] || null)}
              />
              <span className="file-hint">Max File size: 5MB &nbsp;|&nbsp; .pdf .csv .zip .doc .docx .jpeg .jpg .png</span>
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        {/* Products Section */}
        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group full-width" style={{ position: 'relative' }}>
              <label>Search Products:</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder={form.location_id ? "Search products by name / SKU" : "Please select a business location first"}
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  disabled={!form.location_id}
                />
              </div>
              {productSearchTerm && (
                <div
                  className="product-search-dropdown-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    zIndex: 9999,
                    maxHeight: '260px',
                    overflowY: 'auto',
                    marginTop: '4px',
                    padding: '0.25rem 0'
                  }}
                >
                  {isLoadingProducts ? (
                    <div style={{ padding: '0.75rem 1rem', color: '#64748b', fontSize: '0.875rem' }}>⏳ Searching products...</div>
                  ) : productOptions.length === 0 ? (
                    <div style={{ padding: '0.75rem 1rem', color: '#94a3b8', fontSize: '0.875rem', fontStyle: 'italic' }}>No products found</div>
                  ) : (
                    productOptions.map((p) => {
                      const variation = p.product_variations?.[0]?.variations?.[0] || {};
                      const skuVal = p.sku || p.sub_sku || variation.sub_sku || 'N/A';
                      const priceVal = parseFloat(
                        p.purchase_price_inc_tax ||
                        p.default_purchase_price ||
                        variation.dpp_inc_tax ||
                        variation.default_purchase_price ||
                        p.purchase_price ||
                        0
                      );
                      return (
                        <div
                          key={p.id}
                          onClick={() => addProduct(p)}
                          style={{
                            padding: '0.6rem 1rem',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
                        >
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1e293b' }}>
                              {p.name || p.product}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              SKU: <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{skuVal}</span>
                            </div>
                          </div>
                          <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#2563eb' }}>
                            ${priceVal.toFixed(2)}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

            </div>
          </div>

          {errors.products && <div className="error-text mb-2">{errors.products}</div>}

          <div className="product-table" style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: '700px', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '4%' }}>#</th>
                  <th>Product</th>
                  <th style={{ width: '12%' }}>Quantity</th>
                  <th style={{ width: '15%' }}>Unit Price</th>
                  <th style={{ width: '15%' }}>Lot Number</th>
                  <th style={{ width: '15%' }}>Expiry Date</th>
                  <th style={{ width: '15%' }}>Subtotal</th>
                  <th style={{ width: '4%' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.products.length === 0 ? (
                  <tr><td colSpan={8} className="no-products">No products added</td></tr>
                ) : (
                  form.products.map((p, idx) => (
                    <tr key={p.id}>
                      <td style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>{idx + 1}</td>
                      <td style={{ fontWeight: '500', fontSize: '0.875rem' }}>{p.name}</td>
                      <td>
                        <div className="quantity-input-group">
                          <button type="button" className="qty-btn minus" onClick={() => updateProduct(p.id, 'quantity', Math.max(0, (parseFloat(p.quantity) || 0) - 1))}>-</button>
                          <input type="number" className="quantity-input-centered" value={p.quantity} min="0" step="any" onChange={(e) => updateProduct(p.id, 'quantity', e.target.value)} />
                          <button type="button" className="qty-btn plus" onClick={() => updateProduct(p.id, 'quantity', (parseFloat(p.quantity) || 0) + 1)}>+</button>
                        </div>
                      </td>
                      <td><input type="number" className="form-input unit-price-input" value={p.unit_price} min="0" step="any" onChange={(e) => updateProduct(p.id, 'unit_price', e.target.value)} /></td>
                      <td><input type="text" className="form-input" value={p.lot_number} onChange={(e) => updateProduct(p.id, 'lot_number', e.target.value)} /></td>
                      <td><input type="date" className="form-input" value={p.exp_date} onChange={(e) => updateProduct(p.id, 'exp_date', e.target.value)} /></td>
                      <td style={{ textAlign: 'right', paddingRight: '0.5rem', fontWeight: '600' }}>{((parseFloat(p.quantity) || 0) * (parseFloat(p.unit_price) || 0)).toFixed(2)}</td>
                      <td><button type="button" className="remove-product-btn" onClick={() => removeProduct(p.id)}>✕</button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '2rem', padding: '0.75rem 0.5rem', fontSize: '0.9rem', color: '#475569' }}>
            <div>
              <label style={{ fontWeight: '500', marginRight: '0.5rem' }}>Purchase Tax:</label>
              <select className="form-select" style={{ width: 'auto', display: 'inline-block' }} value={form.tax_id} onChange={(e) => handleChange('tax_id', e.target.value)}>
                <option value="">None</option>
                {taxes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.amount}%)</option>
                ))}
              </select>
            </div>
            <div style={{ fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>
              Total Amount: {finalTotal.toFixed(2)}
            </div>
          </div>
        </div>

        <div className="form-section-content action-buttons-section">
          <div className="action-buttons">
            <button type="submit" className="save-btn" disabled={isSaving}>💾 {isSaving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddPurchaseReturn;
